import { Extension } from "@tiptap/react";
import { type Editor } from "@tiptap/react";
import Suggestion, { type SuggestionOptions } from "@tiptap/suggestion";
import { createRoot, type Root } from "react-dom/client";
import { createElement } from "react";
import {
  SlashCommandList,
  type SlashCommandListRef,
} from "./slash-command-list";
import {
  createSlashCommandItems,
  type SlashCommandItem,
} from "./slash-command-items";

const MENU_HEIGHT = 300;
const GAP = 4;

function positionContainer(
  container: HTMLDivElement,
  clientRect: (() => DOMRect | null) | null,
) {
  if (!clientRect) return;
  const rect = clientRect();
  if (!rect) return;

  const spaceBelow = window.innerHeight - rect.bottom;
  const showAbove = spaceBelow < MENU_HEIGHT + GAP;

  container.style.left = `${rect.left}px`;
  container.style.top = showAbove
    ? `${rect.top - GAP}px`
    : `${rect.bottom + GAP}px`;
  container.style.transform = showAbove ? "translateY(-100%)" : "";
}

function createSuggestionRenderer(
  onImageUpload?: (file: File) => Promise<string>,
): SuggestionOptions<SlashCommandItem>["render"] {
  let container: HTMLDivElement | null = null;
  let root: Root | null = null;
  let componentRef: SlashCommandListRef | null = null;

  return () => ({
    onStart: (props) => {
      container = document.createElement("div");
      container.style.position = "fixed";
      container.style.zIndex = "50";
      document.body.appendChild(container);

      root = createRoot(container);

      positionContainer(container, props.clientRect);

      const allItems = createSlashCommandItems(onImageUpload);
      const filtered = allItems.filter((item) =>
        item.title.toLowerCase().includes((props.query ?? "").toLowerCase()),
      );

      root.render(
        createElement(SlashCommandList, {
          items: filtered,
          command: (item: SlashCommandItem) => {
            props.command(item);
          },
          ref: (ref: SlashCommandListRef | null) => {
            componentRef = ref;
          },
        }),
      );
    },

    onUpdate: (props) => {
      if (!root || !container) return;

      positionContainer(container, props.clientRect);

      const allItems = createSlashCommandItems(onImageUpload);
      const filtered = allItems.filter((item) =>
        item.title.toLowerCase().includes((props.query ?? "").toLowerCase()),
      );

      root.render(
        createElement(SlashCommandList, {
          items: filtered,
          command: (item: SlashCommandItem) => {
            props.command(item);
          },
          ref: (ref: SlashCommandListRef | null) => {
            componentRef = ref;
          },
        }),
      );
    },

    onKeyDown: (props) => {
      if (props.event.key === "Escape") {
        if (container) {
          root?.unmount();
          container.remove();
          container = null;
          root = null;
        }
        return true;
      }

      return componentRef?.onKeyDown(props) ?? false;
    },

    onExit: () => {
      if (container) {
        root?.unmount();
        container.remove();
        container = null;
        root = null;
      }
    },
  });
}

export const SlashCommandExtension = Extension.create<{
  onImageUpload?: (file: File) => Promise<string>;
  suggestion: Partial<SuggestionOptions<SlashCommandItem>>;
}>({
  name: "slashCommand",

  addOptions() {
    return {
      onImageUpload: undefined,
      suggestion: {
        char: "/",
        startOfLine: false,
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion<SlashCommandItem>({
        editor: this.editor as Editor,
        char: "/",
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const textBefore = $from.parent.textContent.slice(
            0,
            range.from - $from.start(),
          );
          return textBefore.trim() === "";
        },
        command: ({ editor, range, props: item }) => {
          editor.chain().focus().deleteRange(range).run();
          item.command(editor as Editor);
        },
        items: ({ query }) => {
          const allItems = createSlashCommandItems(this.options.onImageUpload);
          return allItems.filter((item) =>
            item.title.toLowerCase().includes(query.toLowerCase()),
          );
        },
        render: createSuggestionRenderer(this.options.onImageUpload),
      }),
    ];
  },
});
